export const loadScript = (
  name: string,
  entryPointUrl: string,
  versionAutoGenerated = true,
  force = false,
) =>
  new Promise<void>((resolve, reject) => {
    const remoteWindow = window as unknown as Record<string, unknown>;

    if (remoteWindow[name] && !force) {
      // it was already loaded;
      resolve();
      return;
    }

    const scriptId = `mfe-script-${name}`;
    let script = document.getElementById(scriptId) as HTMLScriptElement;
    if (!script || force) {
      script?.remove();
      script = document.createElement('script');
      script.type = 'text/javascript';
      script.id = scriptId;
      script.async = true;
      script.src = versionAutoGenerated
        ? `${entryPointUrl}?v=${new Date().getTime()}`
        : entryPointUrl;

      document.head.appendChild(script);
    }

    const loadListener = () => {
      resolve();
      script.removeEventListener('load', loadListener);
      // eslint-disable-next-line @typescript-eslint/no-use-before-define
      script.removeEventListener('error', errorListener);
    };

    const errorListener = () => {
      reject();
      script.removeEventListener('load', loadListener);
      script.removeEventListener('error', errorListener);
    };

    script.addEventListener('load', loadListener);
    script.addEventListener('error', errorListener);
  });

interface RemoteContainer {
  init?: (shareScope: unknown) => Promise<void> | void;
  get: (exposedPath: string) => Promise<unknown>;
}

export interface PreloadMicroFrontendModuleParams {
  name: string;
  entryPointUrl: string;
  exposedPath: string;
  versionAutoGenerated?: boolean;
}

const preloadedRemoteModules = new Map<string, Promise<void>>();

export const preloadMicroFrontendModule = ({
  name,
  entryPointUrl,
  exposedPath,
  versionAutoGenerated = true,
}: PreloadMicroFrontendModuleParams): Promise<void> => {
  if (!name || !entryPointUrl || !exposedPath) return Promise.resolve();

  const preloadKey = `${name}:${entryPointUrl}:${exposedPath}`;
  const cachedPreload = preloadedRemoteModules.get(preloadKey);
  if (cachedPreload) return cachedPreload;

  const preload = loadScript(name, entryPointUrl, versionAutoGenerated)
    .then(async () => {
      const remoteContainerWindow = window as unknown as Record<
        string,
        RemoteContainer | undefined
      >;
      const container = remoteContainerWindow[name];

      if (!container) {
        throw new Error(`Module '${name}' not found in window object`);
      }

      if (typeof container.init === 'function') {
        try {
          await container.init({});
        } catch {
          // Already initialized by another MFE path.
        }
      }

      await container.get(exposedPath);
    })
    .catch((error) => {
      preloadedRemoteModules.delete(preloadKey);
      throw error;
    });

  preloadedRemoteModules.set(preloadKey, preload);
  return preload;
};
